import { useSelector } from "react-redux";

// Components
import { Paragraph } from "../../typography";
import { StatusPill, WorkerAvatar } from "../../UI";

// Constants
import * as serviceTypes from "../../../constants/serviceTypes.constants";

// Icons
import {
  TireIcon,
  TruckIcon,
  TowingIcon,
  TrailerIcon,
  RoadIcon
} from "../../../SVGS";

// Utils
import {
  convertTimeStampToTime,
  convertTimeStampToDate
} from "../../../utils/timeStampToDate";
import { isEmpty } from "../../../utils";

// Styles
import { ServiceItemStyles } from "./serviceItem.styles";

const MAX_VISIBLE_AVATARS = 3;

const formatVehicleLine = ({ manufacturerName, descriptor, last4Vin }) => {
  const head = [manufacturerName, descriptor].filter(Boolean).join(" ");
  if (!head && !last4Vin) return null;
  if (!last4Vin) return head;
  return head ? `${head} · …${last4Vin}` : `…${last4Vin}`;
};

const ServiceItem = ({
  workers,
  status,
  serviceTypeKey,
  id,
  time,
  eta,
  mobility,
  newMessagesCount,
  driver,
  truck,
  trailer
}) => {
  const { systemComponents } = useSelector(state => state.app);
  const serviceType = systemComponents.serviceTypes.find(
    type => type.typeKey === serviceTypeKey
  );

  const generateIcon = () => {
    switch (serviceTypeKey) {
      case serviceTypes.TRUCK_SHOP:
        return <TruckIcon style={{ width: 16, marginRight: 8 }} />;
      case serviceTypes.ROAD_ASSISTANCE:
        return <RoadIcon style={{ width: 16, marginRight: 8 }} />;
      case serviceTypes.TOWING:
        return <TowingIcon style={{ width: 16, marginRight: 8 }} />;
      case serviceTypes.TIRE_REPAIR:
        return <TireIcon style={{ width: 16, marginRight: 8 }} />;
      case serviceTypes.TRAILER_REPAIR:
        return <TrailerIcon style={{ width: 16, marginRight: 8 }} />;
      default:
        return null;
    }
  };

  const isExternalService = status === "external";

  const truckLine = truck
    ? formatVehicleLine({
        manufacturerName: truck.manufacturer?.manufacturer,
        descriptor: truck.model,
        last4Vin: truck.last4Vin
      })
    : null;

  const trailerLine = trailer
    ? formatVehicleLine({
        manufacturerName: trailer.manufacturer?.manufacturer,
        descriptor: trailer.type?.name,
        last4Vin: trailer.last4Vin
      })
    : null;

  const workerList = workers ?? [];
  const visibleWorkers = workerList.slice(0, MAX_VISIBLE_AVATARS);
  const hiddenWorkersCount = Math.max(
    0,
    workerList.length - MAX_VISIBLE_AVATARS
  );

  return (
    <ServiceItemStyles>
      <Paragraph className="ID index">{id}</Paragraph>

      <div className="status">
        <StatusPill
          style={{ minWidth: "unset", display: "inline-block" }}
          status={status}
        />
      </div>

      <div className="start">
        <Paragraph className="startDate">
          {convertTimeStampToDate(time)}
        </Paragraph>
        <Paragraph className="startDate">
          <span style={{ color: "rgba(86, 86, 86, 1)" }}>
            {convertTimeStampToTime(time)}
          </span>
        </Paragraph>
      </div>

      <div className="finish">
        <Paragraph className="finishDate">
          {convertTimeStampToDate(eta)}
        </Paragraph>
        <Paragraph className="finishDate">
          <span style={{ color: "rgba(0, 102, 68, 1)" }}>
            {convertTimeStampToTime(eta)}
          </span>
        </Paragraph>
      </div>

      <div className="user">
        <Paragraph className="userName">
          {isExternalService
            ? "External user"
            : `${driver?.firstName ?? ""} ${driver?.lastName ?? ""}`.trim() ||
              "—"}
        </Paragraph>
        {newMessagesCount > 0 && (
          <span className="msgBadge" title={`${newMessagesCount} new message(s)`}>
            New message
          </span>
        )}
      </div>

      <div className="serviceType">
        {generateIcon()}
        <Paragraph className="serviceTypeText">
          {serviceType?.name}
          {mobility ? ` · ${mobility}` : ""}
        </Paragraph>
      </div>

      <div className="truck">
        <Paragraph className="vehicleLine" title={truckLine ?? ""}>
          {truckLine ?? "—"}
        </Paragraph>
      </div>

      <div className="trailer">
        <Paragraph className="vehicleLine" title={trailerLine ?? ""}>
          {trailerLine ?? "—"}
        </Paragraph>
      </div>

      <div className="workers">
        {isEmpty(workerList) ? (
          <span className="notAssigned">Not assigned</span>
        ) : (
          <div className="avatarStack">
            {visibleWorkers.map(worker => (
              <WorkerAvatar
                key={worker.id}
                firstName={worker.firstName}
                lastName={worker.lastName}
                profileImage={worker.profileImage}
                size={28}
              />
            ))}
            {hiddenWorkersCount > 0 && (
              <span
                className="moreBadge"
                title={`${hiddenWorkersCount} more worker(s)`}
              >
                +{hiddenWorkersCount}
              </span>
            )}
          </div>
        )}
      </div>
    </ServiceItemStyles>
  );
};

export default ServiceItem;
